/**
 * @file index.tsx
 *
 * @fileoverview Adds blog metatags to the HTML head.
 */

import dynamic from 'next/dynamic';
import { useContext } from 'react';
import {
  CANONICAL_QUERY_PARAM_EXCLUDE_PATTERNS,
  excludeCanonicalPathQueryParamsByPattern,
} from '~/jobiqo/components/Search/utils/searchHelpers';
import { AppContext, PathContext } from '~/jobiqo/context/app';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { Metatag } from '~/jobiqo/types/types';
import { withLocalePrefix } from '~/jobiqo/utils/links';

const Head = dynamic(import('next/head'));

interface BlogMetatagsProps {
  /**
   * The metatags.
   */
  metatags: Metatag[];
}

export default function BlogMetatags({ metatags }: BlogMetatagsProps) {
  const { host } = useContext(PathContext);
  const { locale } = useContext(AppContext);
  const { APP } = useFrontendConfig();

  if (!(metatags && metatags.length > 0)) {
    return null;
  }

  // Removes the title and canonical metatags from the array.
  const pageMetaTags = metatags.filter((metatag) => {
    return metatag.meta !== 'title' && metatag.meta !== 'canonical';
  });

  const titleMetaTag = metatags.find((metatag) => metatag.meta === 'title');
  const canonicalMetaTag = metatags.find(
    (metatag) => metatag.meta === 'canonical',
  );
  const canonicalUrl = canonicalMetaTag?.value
    ? withLocalePrefix(
        excludeCanonicalPathQueryParamsByPattern(
          canonicalMetaTag.value,
          CANONICAL_QUERY_PARAM_EXCLUDE_PATTERNS,
        ),
        host,
        APP.LOCALES,
        locale,
      )
    : null;

  return (
    <Head>
      {pageMetaTags.map((metaInfo: Metatag, index) => {
        switch (metaInfo.meta) {
          case 'title':
            return <title key={metaInfo.meta}>{metaInfo.value}</title>;
          default:
            return (
              metaInfo.meta &&
              metaInfo.value && (
                <meta
                  key={`${metaInfo.meta}`}
                  // og: tags use property and not name.
                  {...(metaInfo.meta.startsWith('og:')
                    ? { property: metaInfo.meta }
                    : { name: metaInfo.meta })}
                  content={metaInfo.value}
                />
              )
            );
        }
      })}
      {titleMetaTag && <title>{titleMetaTag.value}</title>}
      {canonicalUrl && (
        <link rel="canonical" href={canonicalUrl} key="canonical" />
      )}
    </Head>
  );
}
export { BlogMetatags };
